// @ts-nocheck
import { normalizeString } from './accent';
import { normalizeFieldToStandardField } from './fields';
import { type Fields, type HeaderIndex, type ParsedGenericSheet, type RawCell, type Sheet, SheetType } from './types';

type ExcelCellValue = import('exceljs').CellValue;

// We use this function to force cast to custom type
// For now typescript cast object.entries to always [string, T]
function typedObjectEntries<T, G>(Obj: any): Array<[T, G]> {
  return Object.entries(Obj) as unknown as Array<[T, G]>;
}

function parseCellValue(value: ExcelCellValue): RawCell {
  if (value === null || value === undefined) return undefined;
  if (value instanceof Date || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if ('result' in value) return parseCellValue(value.result);
  if ('text' in value) return value.text;
  if ('richText' in value) return value.richText.map(({ text }) => text).join('');
  return undefined;
}

export class Spreadsheet<T> {
  protected parsedSheet: Promise<Sheet<T>>;

  constructor(file: ArrayBuffer, type: any = 'array', indexUserSelectedFields?: string[], standardFields = true) {
    this.parsedSheet = this.parser(file, type, indexUserSelectedFields, standardFields);
  }

  protected async parser(file: ArrayBuffer, type: any = 'array', indexUserSelectedFields?: string[] | undefined, standardFields = true): Promise<Sheet<T>> {
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(file);

    // if (isCobliRoutingWorkbook(workbook.SheetNames)) {
    //   const parsedRouteSheet = parserCobliRoutingSheet<T>(workbook, indexUserSelectedFields, standardFields);
    //
    //   this.parsedSheet = {
    //     type: SheetType.ROUTING,
    //     routes: parsedRouteSheet,
    //   };
    // }

    const firstSheet = workbook.worksheets[0];
    const rows: RawCell[][] = [];

    firstSheet.eachRow((row) => {
      const rowValues = (row.values as ExcelCellValue[]).slice(1).map(parseCellValue);
      if (rowValues.some((value) => value !== undefined && value !== null && value !== '')) {
        rows.push(rowValues);
      }
    });

    const parsedGenericSheet = this.parserGenericSheet<T>(rows, standardFields, indexUserSelectedFields);

    return {
      type: SheetType.GENERIC,
      worksheets: [
        {
          fields: parsedGenericSheet.fields,
          data: parsedGenericSheet.data,
        },
      ],
    };
  }

  public getParsedSheet = () => this.parsedSheet;

  private getIndexFromRawHeaderFields(rawHeaderFields: RawCell[]): HeaderIndex {
    const result: HeaderIndex = {};
    for (let i = 0; i < rawHeaderFields.length; i++) {
      const field = rawHeaderFields[i];
      if (typeof field === 'string') {
        result[field] = [i];
      }
    }
    return result;
  }

  private getIndexFromUserSelectFields(rawHeaderFields: RawCell[], selectFields: RawCell[]): HeaderIndex {
    const result: HeaderIndex = {};
    for (const selectField of selectFields) {
      const indexesOfField = rawHeaderFields
        .map((rawHeaderField, index) => ({ rawHeaderField, index }))
        .filter(({ rawHeaderField }) => rawHeaderField === selectField)
        .map(({ index }) => index);

      if (indexesOfField.length > 0) {
        if (typeof selectField === 'number' || typeof selectField === 'string') {
          result[selectField] = indexesOfField;
        }
      }
    }

    return result;
  }

  private getParsedCellValue(cell?: RawCell) {
    if (typeof cell === 'string') {
      const trimmedStr = cell.trim();
      if (trimmedStr.length === 0) {
        return null;
      }
      return trimmedStr;
    }
    if (typeof cell === 'number') {
      return cell;
    }
    return null;
  }

  private getStandardFieldToRawField<F>(indexNormalizeUserSelectedFields: HeaderIndex, rawFields: RawCell[]) {
    const result: Fields<F> = {};

    typedObjectEntries<F, number[]>(indexNormalizeUserSelectedFields).forEach(([standardField, indexes]) => {
      indexes.forEach((index) => {
        const value = result[standardField];
        const newValue = rawFields[index];
        if (value && Array.isArray(value)) {
          result[standardField] = [...value, newValue];
        } else if (typeof newValue === 'string' || typeof newValue === 'number') {
          result[standardField] = newValue;
        }
      });
    });

    return result;
  }

  /*
      This spreadsheet is more simple and is like a big simple table and only one workbook(tab)
      There is no additional information.
  */
  protected parserGenericSheet<F>([rawFields, ...rawRowValues]: RawCell[][], enableStandardFields: boolean, rawUserSelectedFields: string[] = []): ParsedGenericSheet<F> {
    const normalizedUserSelectedFields = rawUserSelectedFields.map((field) => normalizeString(field.toString()));

    let fields: string[] = rawFields.map((field) => normalizeString(field.toString()));
    if (enableStandardFields) {
      fields = fields.map(normalizeFieldToStandardField);
    }

    const indexUserSelectedFields =
      normalizedUserSelectedFields.length === 0 ? this.getIndexFromRawHeaderFields(fields) : this.getIndexFromUserSelectFields(fields, normalizedUserSelectedFields);

    const indexNormalizeUserSelectedFields = indexUserSelectedFields;

    const parsedSheet = [];

    for (const rawCells of rawRowValues) {
      const parsedRow: Fields<F> = {};

      typedObjectEntries<F, number[]>(indexUserSelectedFields).forEach(([field, indexesOfField]) => {
        indexesOfField.forEach((indexOfField: number) => {
          const rawNewValue = rawCells[indexOfField];
          const newCell = this.getParsedCellValue(rawNewValue);
          const actualCellValue = parsedRow[field];

          /*
            Code bellow first try to save a primitive value like string, integer
            but if it's already has a primitive value, put actual value and new value inside a array
            and if it's a array, it's only needs update array with new value.
          */

          if (actualCellValue && Array.isArray(actualCellValue)) {
            parsedRow[field] = [...actualCellValue, newCell];
          } else {
            parsedRow[field] = newCell;
          }
        });
      });
      const hasOneValidValueAtLeast = Object.values(parsedRow).find(Boolean);
      if (hasOneValidValueAtLeast) {
        parsedSheet.push(parsedRow);
      }
    }

    return {
      fields: this.getStandardFieldToRawField<F>(indexNormalizeUserSelectedFields, rawFields),
      data: parsedSheet,
    };
  }
}
